import React, { useState, useEffect } from 'react';
import useGroupLabels from 'hooks/useGroupLabels';
import useOrganisationWords from 'hooks/useOrganisationWords';
import { formatGroupName } from 'utils/groupLabel';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import styles from './ClubRecordApprovalPopup.module.css';
import BackIcon from 'assets/images/icon_arrow_right.svg';
import CloseIcon from 'assets/images/icon_close.svg';
// Reject marks the result rejected; nothing is deleted, so a cross, not a bin (Doug, 2026-10-24).
import { ReactComponent as CrossIcon } from 'assets/images/icon_cross.svg';
import CustomButton, {
  defaultButtonStyle,
} from 'components/common/CustomButton';
import IconTextButton from 'components/common/IconTextButton';
import { redColor, whiteColor } from 'config/global';
import CustomNewConfirm from 'components/NewDesign/CustomNewConfirm';
import { convertDateTime, convertMillisecondsToRecord } from 'utils/time';
import { Link } from 'react-router-dom';
import { ReactComponent as EyeIcon } from 'assets/images/icon_eye.svg';
import { navigateToEventViewForClubRecord } from 'services/admin/ResultService';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

const ClubRecordApprovalPopup = ({
  clubID,
  showPopup,
  closePopup,
  selectedClubRecordResult,
  changeStatus,
}) => {
  // The group a record is for reads as a number under "Age", or as the year (Y5) under "Year" for a club that
  // groups by year level.
  const { groupingBasis, words } = useGroupLabels(clubID);
  // "the club record", or "the school record" for a school.
  const { word: organisationWord } = useOrganisationWords(clubID);
  const [confirmState, setConfirmState] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [officialName, setOfficialName] = useState('');
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Load the official name when the popup opens or record changes
  useEffect(() => {
    if (selectedClubRecordResult?.eventOfficialName) {
      setOfficialName(selectedClubRecordResult.eventOfficialName);
    } else {
      setOfficialName('');
    }
  }, [selectedClubRecordResult]);

  const openConfirmPopup = () => {
    setConfirmState(true);
  };

  const closeConfirmPopup = () => {
    setConfirmState(false);
  };

  const handleApproveRecord = async () => {
    // Validate official name is provided
    if (!officialName.trim()) {
      toast.error("Please enter the approving official's name");
      return;
    }

    setIsLoading(true);

    try {
      await changeStatus('Approved', officialName.trim());
      // Success - the parent function handles the success toast message
    } catch (err) {
      console.error('Error approving record:', err);

      // Handle error responses (400, 500, etc.)
      const errorMessage =
        err?.response?.data || err?.message || 'Failed to approve record';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const discardRecord = async () => {
    // Validate official name is provided
    if (!officialName.trim()) {
      toast.error(
        "Please enter the official's name who is rejecting this record"
      );
      closeConfirmPopup(); // Close the confirm dialog
      return;
    }

    setIsLoading(true);

    try {
      await changeStatus('Rejected', officialName.trim()); // ← Pass the official name
      // Success - the parent function handles the success toast message
      setConfirmState(false);
    } catch (err) {
      console.error('Error rejecting record:', err);

      // Handle error responses (400, 500, etc.)
      const errorMessage =
        err?.response?.data || err?.message || 'Failed to reject record';
      toast.error(errorMessage);
      setConfirmState(false);
    } finally {
      setIsLoading(false);
    }
  };

  const gotoEvent = () => {
    console.log('handle clicking', selectedClubRecordResult);
    navigateToEventViewForClubRecord(
      clubID,
      selectedClubRecordResult,
      navigate,
      dispatch
    );
  };

  const handleClosePopup = () => {
    // Clear loading state when closing popup
    setIsLoading(false);
    closePopup();
  };

  const handleOfficialNameChange = (e) => {
    setOfficialName(e.target.value);
  };

  // Helper function to get track surface badge
  const getTrackSurfaceBadge = (surface) => {
    if (!surface) return null;

    const surfaceClass = surface.toLowerCase();
    return (
      <span className={`${styles.trackSurfaceBadge} ${styles[surfaceClass]}`}>
        {surface}
      </span>
    );
  };

  // Helper function to get the no record text
  const getNoRecordText = () => {
    const { gender, athleteAge, eventType } =
      selectedClubRecordResult || {};

    return `No ${organisationWord} record exists for ${eventType} ${gender} ${formatGroupName(groupingBasis, athleteAge)}`;
  };

  // Helper function to get approval context text
  const getApprovalText = () => {
    const { trackSurface, currentRecordSurface, currentRecordHolderName } =
      selectedClubRecordResult || {};

    // If there's no current record
    if (!currentRecordHolderName) {
      return `Approving this record will set it as the ${organisationWord} record. Rejecting the record will remove it from the pending records list.`;
    }

    // If surfaces are different
    if (trackSurface && currentRecordSurface && trackSurface !== currentRecordSurface) {
      return `Approving this record will set it as the ${organisationWord} record for ${trackSurface} surface. The existing ${currentRecordSurface} surface record will remain separate. Rejecting the record will remove it from the pending records list.`;
    }

    // Default message (same surface or no surface for field events)
    return `Approving this record will replace the current ${organisationWord} record. Rejecting the record will remove it from the pending records list.`;
  };

  return (
    <div
      className={`${styles.clubRecordApprovalPopupOverlay} ${
        showPopup ? styles.active : ''
      }`}
    >
      <div className={styles.clubRecordApprovalPopup}>
        <div className={styles.clubRecordApprovalPopupContainer}>
          <div className={styles.title}>{'Review Pending Record'}</div>
          <div className={styles.subtitle}>
            {`${selectedClubRecordResult.gender} ${formatGroupName(groupingBasis, selectedClubRecordResult.athleteAge)} ${selectedClubRecordResult.eventType}`}
          </div>

          <div className={styles.contentWrapper}>
            {/* Current Record Section */}
            <div className={styles.currentRecordSection}>
              <div className={styles.currentRecordTitle}>Current Record</div>
              {selectedClubRecordResult?.currentRecordHolderName ? (
                <div className={styles.currentRecordValueContainer}>
                  <span className={styles.currentRecordValue}>
                    {selectedClubRecordResult?.currentRecordHolderName}{' '}
                    {selectedClubRecordResult?.currentRecordValue != null &&
                      (selectedClubRecordResult?.unit === 'm'
                        ? `${selectedClubRecordResult?.currentRecordValue} m`
                        : convertMillisecondsToRecord(
                            selectedClubRecordResult?.currentRecordValue,
                            2
                          ))}{' '}
                    -{' '}
                    {convertDateTime(
                      selectedClubRecordResult?.currentRecordAchievedAt
                    ).date || ''}
                  </span>
                  {selectedClubRecordResult?.currentRecordSurface && (
                    <>
                      {' '}
                      {getTrackSurfaceBadge(
                        selectedClubRecordResult.currentRecordSurface
                      )}
                    </>
                  )}
                </div>
              ) : (
                <div className={styles.currentRecordValue}>
                  {getNoRecordText()}
                </div>
              )}
            </div>

            {/* New Record Section */}
            <div className={styles.recordSection}>
              <div className={styles.recordSectionTitle}>New Record</div>
              <div className={styles.table}>
                <div className={styles.tableWrapper}>
                  <div className={styles.tableHeader}>
                    <div className={styles.headerCell}>Event</div>
                    <div className={styles.headerCell}>{words.value}</div>
                    <div className={styles.headerCell}>Gender</div>
                    <div className={styles.headerCell}>Result</div>
                    <div className={styles.headerCell}>Athlete</div>
                    <div className={styles.headerCell}>Date</div>
                  </div>
                  <div className={styles.tableBody}>
                    <div className={styles.tableRow}>
                      <div className={styles.bodyCell}>
                        <div className={styles.eventCellContent}>
                          <div className={styles.eventWithBadge}>
                            {selectedClubRecordResult?.eventType}
                            {selectedClubRecordResult?.trackSurface && (
                              getTrackSurfaceBadge(
                                selectedClubRecordResult.trackSurface
                              )
                            )}
                          </div>
                          <IconTextButton
                            text=""
                            icon={<EyeIcon />}
                            onClick={() => gotoEvent()}
                            textColor={redColor}
                            borderColor="white"
                            height="unset"
                            iconPadding="5px"
                            iconColor={redColor}
                            iconPosition="right"
                            style={{
                              fontWeight: 'bold',
                              marginLeft: '8px',
                            }}
                          />
                        </div>
                      </div>
                      <div className={styles.bodyCell}>
                        {formatGroupName(groupingBasis, selectedClubRecordResult?.athleteAge)}
                      </div>
                      <div className={styles.bodyCell}>
                        {selectedClubRecordResult?.gender}
                      </div>
                      <div className={styles.bodyCell}>
                        {selectedClubRecordResult?.result}
                      </div>
                      <div className={styles.bodyCell}>
                        <Link
                          className="tableCellLink"
                          to={`/athlete-results?athleteId=${selectedClubRecordResult?.athleteId}`}
                        >
                          {selectedClubRecordResult?.athleteName}
                        </Link>
                      </div>
                      <div className={styles.bodyCell}>
                        {convertDateTime(selectedClubRecordResult?.meetDate)
                          .date || ''}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Official Name Field */}
              <div className={styles.officialSection}>
                <div className={styles.officialLabel}>
                  Approving Official Name & Notes{' '}
                  <span className={styles.required}>*</span>
                </div>
                <input
                  type="text"
                  value={officialName}
                  onChange={handleOfficialNameChange}
                  placeholder="Enter official's name and approve/reject notes..."
                  className={styles.officialInput}
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>

          <div className={styles.popupText}>
            {getApprovalText()}
          </div>

          <div className={styles.buttons}>
            <CustomButton
              style={{
                ...defaultButtonStyle,
                marginBottom: 9,
                width: 274,
                fontSize: 16,
                fontWeight: 500,
                opacity: isLoading ? 0.6 : 1,
              }}
              onClick={handleApproveRecord}
              disabled={isLoading}
            >
              {isLoading ? 'Approving...' : 'Approve Record'}
            </CustomButton>
          </div>

          <div className={styles.bottomButton}>
            <IconTextButton
              text={isLoading ? 'Processing...' : 'Reject Record'}
              icon={<CrossIcon />}
              height="50px"
              iconPadding="5px"
              iconPosition="left"
              textColor={redColor}
              borderColor={whiteColor}
              iconColor={redColor}
              iconSize="24px"
              textStyle={{
                fontSize: '16px',
                fontWeight: '600',
              }}
              bgColor="transparent"
              onClick={openConfirmPopup}
              disabled={isLoading}
            />
          </div>

          <div className={styles.goBack} onClick={handleClosePopup}>
            <div className={styles.goBackImage}>
              <img src={BackIcon} alt="back" />
            </div>
            <div className={styles.goBackText}>Back</div>
          </div>
        </div>

        <div className={styles.closeBtn} onClick={handleClosePopup}>
          <img src={CloseIcon} alt="close" />
        </div>
      </div>

      {/* Reject's confirmation, the site's own dialog (as Delete Record on Club Records). A Reject deletes nothing:
          the result is marked rejected and the next result is put forward (docs/club-records-redesign.md, 4.2). */}
      <CustomNewConfirm
        isOpen={confirmState}
        onClose={closeConfirmPopup}
        title="Reject Pending Record"
        description={`Are you sure you want to reject this result as a ${organisationWord} record?`}
        confirmButtonText="Reject Record"
        confirmButtonVariant="danger"
        onConfirm={discardRecord}
        cancelButtonText="Cancel"
        showImpactSummary={true}
        impactItems={[
          {
            label: 'Event',
            value: `${selectedClubRecordResult?.eventType || ''}${
              selectedClubRecordResult?.trackSurface ? ` (${selectedClubRecordResult.trackSurface})` : ''
            }`,
          },
          { label: 'Athlete', value: String(selectedClubRecordResult?.athleteName || '') },
          { label: 'Result', value: String(selectedClubRecordResult?.result || '') },
          {
            label: words.value,
            value: String(formatGroupName(groupingBasis, selectedClubRecordResult?.athleteAge) || ''),
          },
        ]}
        showWarning={true}
        warningText={`A rejected result is never put forward as a ${organisationWord} record again. If another result this season beats the record, the best one takes its place under Pending Records.`}
        isLoading={isLoading}
      />
    </div>
  );
};

ClubRecordApprovalPopup.propTypes = {
  clubID: PropTypes.string,
  showPopup: PropTypes.bool,
  closePopup: PropTypes.func,
  selectedClubRecordResult: PropTypes.object,
  changeStatus: PropTypes.func,
};

export default ClubRecordApprovalPopup;